import Color from 'color';
import type {
    BarSeriesOption,
    EChartsOption,
    LegendOption,
    LineSeriesOption,
    SeriesOption,
    XAXisOption,
} from 'echarts/types/dist/shared';
import { cloneDeep, omit } from 'lodash';
import { getFramework } from '@/framework';
import { ChartType, DEFAULT_FONT_FAMILY, IChartData, IChartLayer, IChartOptions, LayerType } from '@/version-types';
import { nextColorGetter } from './chart-color';
import { isChartTestMode } from './consts';

export const loadEcharts = (() => {
    let _echarts: typeof import('echarts/core') | null = null;
    const loadEcharts = async () => {
        if (_echarts) return _echarts;
        const [echarts, components, features, charts, renderers] = await Promise.all([
            import('echarts/core') as unknown as typeof import('echarts/core'),
            import('echarts/components') as unknown as typeof import('echarts/components'),
            import('echarts/features') as unknown as typeof import('echarts/features'),
            import('echarts/charts') as unknown as typeof import('echarts/charts'),
            import('echarts/renderers') as unknown as typeof import('echarts/renderers'),
        ]);
        // 引入标题，提示框，直角坐标系，数据集，内置数据转换器组件
        echarts.use([
            components.TooltipComponent,
            components.GridComponent,
            components.DatasetComponent,
            components.TransformComponent,
            components.LegendComponent,
            charts.BarChart,
            charts.LineChart,
            charts.PieChart,
            // 标签自动布局
            features.LabelLayout,
            // 全局过渡动画
            features.UniversalTransition,
            renderers.SVGRenderer,
            renderers.CanvasRenderer,
        ]);
        _echarts = echarts;
        return echarts;
    };
    return loadEcharts;
})();

export const getActiveChartLayer = (frameworkIdentity: string): IChartLayer | null => {
    const framework = getFramework(frameworkIdentity);
    const activePickLayer = framework.operate.getActivePickLayer();
    if (activePickLayer?.layerType === LayerType.CHART) return activePickLayer as IChartLayer;
    return null;
};

interface FontItem {
    fontFamily: string;
    text: string;
}

export const getFontsByPosition = (
    chartOptions: IChartOptions,
    position: 'categoryAxisLabel' | 'valueAxisLabel' | 'legend' | 'all',
): FontItem[] => {
    const result: FontItem[] = [];
    if (['categoryAxisLabel', 'all'].includes(position) && chartOptions.categoryAxisLabel.show) {
        const axisLabelFontFamily = chartOptions.categoryAxisLabel.textStyle?.fontFamily ?? DEFAULT_FONT_FAMILY().name;
        const textAxisLabelFontList =
            chartOptions.chartData[0]?.slice(1).map((item) => ({
                text: item === null ? '' : `${item}`,
                fontFamily: axisLabelFontFamily,
            })) ?? [];
        result.push(...textAxisLabelFontList);
    }
    if (['valueAxisLabel', 'all'].includes(position) && chartOptions.valueAxisLabel.show) {
        const axisLabelFontFamily = chartOptions.valueAxisLabel.textStyle?.fontFamily ?? DEFAULT_FONT_FAMILY().name;
        const valueAxisLabelFontItem = {
            text: '0123456789',
            fontFamily: axisLabelFontFamily,
        };
        result.push(valueAxisLabelFontItem);
    }
    if (['legend', 'all'].includes(position) && chartOptions.legend.show) {
        const fontFamily = chartOptions.legend.textStyle.fontFamily ?? DEFAULT_FONT_FAMILY().name;
        const legendFontList =
            chartOptions.chartData[0]?.slice(1).map((item) => ({
                text: item === null ? '' : `${item}`,
                fontFamily,
            })) ?? [];
        result.push(...legendFontList);
    }
    return result;
};

/** 编辑数据后, 保证colors的个数与图表的类别数一致 */
export const getFixedColorsAfterEdit = (
    chartOptions: Pick<IChartOptions, 'type' | 'colors' | 'chartData'>,
): string[] => {
    if (chartOptions.chartData.length === 0) return [];

    const colorAxis = chartOptions.type.includes('pie') ? 'categoryAxis' : 'valueAxis';
    const newDataSeriesCount =
        colorAxis === 'valueAxis' ? chartOptions.chartData[0].length - 1 : chartOptions.chartData.length - 1;
    const oldDataSeriesCount = chartOptions.colors.length;

    if (newDataSeriesCount <= oldDataSeriesCount) {
        return chartOptions.colors.slice(0, newDataSeriesCount);
    }
    const appendCount = newDataSeriesCount - oldDataSeriesCount;
    const newColors = [...chartOptions.colors];
    for (let i = 0; i < appendCount; i++) {
        const newColor = nextColorGetter.useNextColor();
        newColors.push(newColor.hexa());
    }
    return newColors;
};

// echarts没有暴露该类型, 自己手动获取
type CartesianAxisOption = Omit<XAXisOption, 'mainType'>;

interface ExtraOpts {
    type: ChartType;
    chartWidth: number;
    chartHeight: number;
}

type ChartLibType = 'bar' | 'line' | 'pie';

export const getBarGapInfo = (seriesCount: number) => {
    /** 柱状组之前的宽度占柱状宽度的几倍 */
    const barGapWidthPct = 1;
    return {
        barGapWidthPct,
        barCategoryGap: 1 / (seriesCount + 1),
        barGap: 0.1,
    };
};

const getTextAboutWidthRatio = (() => {
    const widthRatioCacheMap = new Map<string, { one: number; num: number; other: number }>();
    const getTextAboutWidthRatio = (text: string, fontFamily: string) => {
        const oneCount = text.split('').filter((t) => t === '1').length;
        const numCount = text.split('').filter((t) => t !== '1' && !isNaN(Number(t))).length;
        const otherCount = text.split('').filter((t) => isNaN(Number(t))).length;
        const cache = widthRatioCacheMap.get(fontFamily);
        if (cache) return cache.one * oneCount + cache.num * numCount + cache.other * otherCount;

        const div = document.createElement('div');
        document.body.append(div);
        const testFontSize = 12;
        div.style.opacity = '0';
        div.style.fontFamily = fontFamily;
        div.style.width = 'max-content';
        div.style.height = 'max-content';
        div.style.fontSize = `${testFontSize}px`;
        div.innerText = '1';
        const oneRatio = div.clientWidth / testFontSize;
        div.innerText = '0';
        const numRatio = div.clientWidth / testFontSize;
        div.innerText = '零';
        const otherRatio = div.clientWidth / testFontSize;
        document.body.removeChild(div);
        div.remove();
        widthRatioCacheMap.set(fontFamily, {
            one: oneRatio,
            num: numRatio,
            other: otherRatio,
        });
        return oneRatio * oneCount + numRatio * numCount + otherRatio * otherCount;
    };

    return getTextAboutWidthRatio;
})();

export const getEchartsOptions = (chartOptions: IChartOptions, extraOpts: ExtraOpts): EChartsOption => {
    const getItemTexts = (chartData: IChartData, type: ChartLibType) =>
        chartData
            .slice(1)
            .map((row) => row[0])
            .map((item) => (item === null ? '' : `${item}`));
    const getSerieTexts = (chartData: IChartData, type: ChartLibType) =>
        chartData[0].slice(1).map((item) => (item === null ? '' : `${item}`));
    const getLegendTexts = (chartData: IChartData, type: ChartLibType) =>
        type === 'pie' ? getItemTexts(chartData, type) : getSerieTexts(chartData, type);

    const getFixedChartData = (chartData: IChartData, type: ChartLibType): IChartData => {
        if (chartData.length === 0) return [];

        // 饼图得去掉多余的系列, 不然echarts会重叠多份饼图 (选中某项时样式就很有问题)
        const newChartData = cloneDeep(type === 'pie' ? chartData.map((row) => row.slice(0, 2)) : chartData);

        // 堆叠柱状图时, 如果第一行第一列不是有值的字符串, echarts会出现展示bug
        if (type !== 'pie') {
            newChartData[0][0] = '-';
        }

        // 去掉字符串的前后空字符
        newChartData.forEach((row, rIdx) => {
            row.forEach((cell, cIdx) => {
                if (typeof cell === 'string') {
                    newChartData[rIdx][cIdx] === cell.trim();
                }
            });
        });

        // 保证系列名/项目名都为字符串, 其他皆为数字
        newChartData.forEach((row, rIdx) => {
            row.forEach((cell, cIdx) => {
                if (rIdx === 0 || cIdx === 0) {
                    newChartData[rIdx][cIdx] = cell === null ? '' : `${cell}`;
                } else if (isNaN(Number(cell))) {
                    newChartData[rIdx][cIdx] = 0;
                }
            });
        });

        const serieTextInfos = getSerieTexts(newChartData, type).map((text, index) => ({ text, index }));
        const itemTextInfos = getItemTexts(newChartData, type).map((text, index) => ({ text, index }));

        // 保证系列名/项目名都唯一 (相同的系列名/项目名在echarts中会不展示, 但业务要展示)
        newChartData.forEach((row, rIdx) => {
            row.forEach((cell, cIdx) => {
                if (rIdx === 0 && cIdx !== 0) {
                    const currentTextsIndex = cIdx - 1;
                    const repeatCount = serieTextInfos.filter(
                        ({ text, index }) => text === cell && index < currentTextsIndex,
                    ).length;
                    newChartData[rIdx][cIdx] = cell + Array(repeatCount).fill(' ').join('');
                }
                if (cIdx === 0 && rIdx !== 0) {
                    const currentTextsIndex = rIdx - 1;
                    const repeatCount = itemTextInfos.filter(
                        ({ text, index }) => text === cell && index < currentTextsIndex,
                    ).length;
                    newChartData[rIdx][cIdx] = cell + Array(repeatCount).fill(' ').join('');
                }
            });
        });

        return newChartData;
    };

    const { colors, legend, categoryAxisLabel, valueAxisLabel, grid, series } = chartOptions;
    const type = extraOpts.type.split('-')[0] as ChartLibType;
    const chartData = getFixedChartData(chartOptions.chartData, type);
    const getSelectOpts = (type: ChartLibType, baseColor: string): Pick<SeriesOption, 'select' | 'selectedMode'> => {
        if (type === 'bar') {
            const baseColorObj = Color(baseColor);
            const newLightness = baseColorObj.lightness() - 15;
            const resultColorObj = baseColorObj.lightness(newLightness);
            return {
                selectedMode: 'series',
                select: {
                    disabled: false,
                    itemStyle: {
                        color: resultColorObj.hexa(),
                        borderWidth: 0,
                        borderColor: '#00000000',
                    },
                },
            };
        }
        if (type === 'pie') {
            return {
                selectedMode: true,
            };
        }
        return {};
    };
    const isHorizontalChart = extraOpts.type.includes('horizontal');
    const isStackedChart = extraOpts.type.includes('stacked');
    const horizontalBasePadding = 0.05;
    const verticalBasePadding = 0.05;
    const abortTextHeightRatio = 1.4;
    const baseLayout: Record<'top' | 'right' | 'bottom' | 'left', number> = {
        top: extraOpts.chartHeight * verticalBasePadding,
        right: extraOpts.chartWidth * horizontalBasePadding,
        bottom: extraOpts.chartHeight * verticalBasePadding,
        left: extraOpts.chartWidth * horizontalBasePadding,
    };
    const legendOption = (() => {
        const legendOption: LegendOption = {
            show: legend.show,
            icon: 'circle',
            padding: 0,
            textStyle: legend.textStyle,
            itemWidth: legend.textStyle.fontSize,
            itemHeight: legend.textStyle.fontSize,
            type: 'scroll',
            orient: ['top', 'bottom'].includes(legend.position) ? 'horizontal' : 'vertical',
            /* eslint-disable-next-line */
            // @ts-ignore
            pageIconSize: legend.textStyle.fontSize / 2,
            pageTextStyle: legend.textStyle,
        };
        if (legend.position) {
            legendOption[legend.position] = Math.round(baseLayout[legend.position]);
            if (['left', 'right'].includes(legend.position)) {
                legendOption.top = 'center';
            }
        }
        return legendOption;
    })();
    const categoryAxisOption: CartesianAxisOption = {
        type: 'category',
        axisLabel: {
            show: categoryAxisLabel.show,
            hideOverlap: true,
            ...categoryAxisLabel.textStyle,
        },
        splitLine: {
            show: grid.show,
        },
        axisTick: { show: false },
        axisLine: { show: true },
    };
    const legendTexts = getLegendTexts(chartData, type);
    const legendTextOfMaxWidth = legendTexts
        .map((t) => t.trim())
        .map((t) => ({ length: t.length, text: t }))
        .reduce((res, cur) => (cur.length >= res.length ? cur : res)).text;
    const maxValue = chartData.slice(1).reduce((max, row) => Math.max(max, ...row.slice(1).map(Number)), 0);
    const valueAxisOption: CartesianAxisOption = {
        axisLabel: {
            show: valueAxisLabel.show,
            hideOverlap: true,
            ...valueAxisLabel.textStyle,
        },
        splitLine: {
            show: grid.show,
        },
        axisTick: { show: false },
        axisLine: { show: true },
    };
    const gridLayout = (() => {
        const gridLayout: Record<'top' | 'right' | 'bottom' | 'left', number> = { ...baseLayout };
        if (legend.show) {
            const getLegendWidth = () => {
                const legendIconWidthRatio = 1;
                const gapWidthRatio = 0.5;
                const minWidth = legend.textStyle.fontSize * 3.8;
                return Math.max(
                    minWidth,
                    legend.textStyle.fontSize *
                        getTextAboutWidthRatio(
                            legendTextOfMaxWidth,
                            legend.textStyle.fontFamily ?? DEFAULT_FONT_FAMILY().name,
                        ) +
                        gapWidthRatio * legend.textStyle.fontSize +
                        legendIconWidthRatio * legend.textStyle.fontSize,
                );
            };
            if (type === 'pie') {
                const otherSide = {
                    top: 'bottom',
                    left: 'right',
                    right: 'left',
                    bottom: 'top',
                }[legend.position];
                // 图例与图表为0.7个字体大小的间距
                gridLayout[otherSide] += legend.textStyle.fontSize * 0.7;
                if (['left', 'right'].includes(legend.position)) {
                    /** 图例到图表的间距 */
                    const paddingWidth = extraOpts.chartWidth * 0.01;
                    gridLayout[legend.position] += getLegendWidth() + paddingWidth;
                } else {
                    gridLayout[legend.position] += legend.textStyle.fontSize * abortTextHeightRatio * (1 + 0.5);
                }
            } else {
                if (['top', 'bottom'].includes(legend.position)) {
                    // 图例与图表为0.3个字体大小的间距
                    gridLayout[legend.position] += legend.textStyle.fontSize * abortTextHeightRatio * (1 + 0.3);
                } else {
                    /** 图例到图表的间距 */
                    const paddingWidth = extraOpts.chartWidth * 0.01;
                    gridLayout[legend.position] += getLegendWidth() + paddingWidth;
                }
            }
        }
        if (type !== 'pie') {
            const horizontalAxisLabel = isHorizontalChart ? categoryAxisLabel : valueAxisLabel;
            if (horizontalAxisLabel.show) {
                // 横轴大约1.5倍字体高度的倍数
                gridLayout.bottom += horizontalAxisLabel.textStyle.fontSize * abortTextHeightRatio * 1.5;
                gridLayout.left +=
                    `${maxValue}`.length *
                    horizontalAxisLabel.textStyle.fontSize *
                    getTextAboutWidthRatio(`${maxValue}`, legend.textStyle.fontFamily ?? DEFAULT_FONT_FAMILY().name);
            }
        }
        gridLayout.top = Math.round(gridLayout.top);
        gridLayout.right = Math.round(gridLayout.right);
        gridLayout.bottom = Math.round(gridLayout.bottom);
        gridLayout.left = Math.round(gridLayout.left);
        return gridLayout;
    })();

    /** 顺时针左上，右上，右下，左下 */
    const getChartLibBorderRadius = (borderRadius: number | undefined): number[] | undefined => {
        if (!borderRadius) return undefined;

        if (isHorizontalChart) {
            return [0, borderRadius, borderRadius, 0];
        }
        return [borderRadius, borderRadius, 0, 0];
    };

    const getAxisOpts = (type: ChartLibType): Pick<EChartsOption, 'xAxis' | 'yAxis'> => {
        if (type === 'pie') return {};
        return {
            xAxis: isHorizontalChart ? valueAxisOption : categoryAxisOption,
            yAxis: isHorizontalChart ? categoryAxisOption : valueAxisOption,
        };
    };

    const lineRestOpts: LineSeriesOption =
        type === 'line'
            ? {
                  symbolSize: extraOpts.chartWidth * 0.01,
                  lineStyle: {
                      width: extraOpts.chartWidth * 0.006,
                  },
              }
            : {};

    const barRestOpts: BarSeriesOption =
        type === 'bar'
            ? {
                  barCategoryGap: `${getBarGapInfo(legendTexts.length).barCategoryGap * 100}%`,
                  barGap: `${getBarGapInfo(legendTexts.length).barGap * 100}%`,
              }
            : {};

    const getSeriesLabelOpts = () => {
        const textStyle =
            type === 'pie'
                ? {
                      ...series.label.textStyle,
                      lineHeight: series.label.textStyle.fontSize * 1.5,
                      align: 'center',
                      bleedMargin: 0,
                      // echarts的默认行为-超出截断省略号有bug, 少数情况下会出现直接截断不出省略号, 不能截断
                      overflow: 'none',
                  }
                : type === 'bar'
                  ? // 柱状图的数据展示采用inside模式, 所以用echarts自带的颜色计算, 不用设置的颜色
                    omit(series.label.textStyle, 'color')
                  : { ...series.label.textStyle };
        return type === 'pie'
            ? {
                  show: series.label.show,
                  position: 'outside',
                  formatter: '{@[1]}',
                  rich: {
                      value: textStyle,
                  },
                  ...textStyle,
              }
            : {
                  show: series.label.show,
                  position: type === 'line' ? 'top' : isHorizontalChart ? 'insideLeft' : 'insideBottom',
                  ...textStyle,
              };
    };

    const pieTextGap = series.label.textStyle.fontSize * 0.6;
    const pieLineLength = series.label.textStyle.fontSize * 0.8;

    const getPieRadius = (): [string, string] => {
        if (!chartOptions.series.label.show) {
            return [chartOptions.type === 'pie-doughnut' ? '50%' : '0%', '100%'];
        }
        // echarts饼图的文字不能超出grid的竖直区域, 所以直接给文字腾位置
        const textHeight = series.label.textStyle.fontSize * abortTextHeightRatio;
        const gridHeight = extraOpts.chartHeight * (1 - 2 * verticalBasePadding);
        const pieWidthRatio = 1 - (2 * textHeight) / gridHeight;
        return [
            chartOptions.type === 'pie-doughnut' ? `${(pieWidthRatio / 2) * 100}%` : '0%',
            `${pieWidthRatio * 100}%`,
        ];
    };

    return {
        color: colors,
        legend: legendOption,
        grid: {
            show: type === 'pie' ? isChartTestMode : grid.show,
            ...gridLayout,
        },
        ...getAxisOpts(type),
        dataset: {
            source: chartData,
        },
        series: chartData[0].slice(1).map<SeriesOption>((_, index) => {
            const isLastIndex = chartData[0].slice(1).length - 1 === index;
            const color = colors[index];
            return {
                type,
                // 随便给个值就行, 这里给'a'
                stack: isStackedChart ? 'a' : undefined,
                ...getSelectOpts(type, color),
                ...(lineRestOpts as any),
                ...barRestOpts,
                ...(type === 'pie' ? gridLayout : {}),
                itemStyle: {
                    borderRadius: isStackedChart
                        ? isLastIndex
                            ? getChartLibBorderRadius(series.itemStyle?.borderRadius)
                            : 0
                        : getChartLibBorderRadius(series.itemStyle?.borderRadius),
                },
                label: getSeriesLabelOpts(),
                labelLine:
                    type === 'pie'
                        ? {
                              length: pieLineLength / 2,
                              length2: pieLineLength / 2,
                              minTurnAngle: 120,
                              distanceToLabelLine: pieTextGap,
                          }
                        : undefined,
                ...(type === 'pie' ? { radius: getPieRadius() } : {}),
            };
        }),
        tooltip: {
            show: true,
            trigger: type === 'line' ? 'axis' : 'item',
            padding: [categoryAxisLabel.textStyle.fontSize * 0.5, categoryAxisLabel.textStyle.fontSize * 0.8],
            textStyle: {
                fontFamily: categoryAxisLabel.textStyle.fontFamily,
                fontSize: categoryAxisLabel.textStyle.fontSize,
                color: categoryAxisLabel.textStyle.color,
            },
            // formatter: (_params) => {
            //     const { seriesName, name, value: valueProp, encode, color } = _params as CallbackDataParams;
            //     const dotW = extraOpts.chartWidth * 0.02;
            //     const value =
            //         type === 'pie'
            //             ? /* eslint-disable-next-line */
            //               // @ts-ignore
            //               valueProp[encode.value[0]]
            //             : /* eslint-disable-next-line */
            //               // @ts-ignore
            //               valueProp[encode[isHorizontalChart ? 'x' : 'y'][0]];
            //     const dotHTML = `<span style="display:inline-block;margin-right:${dotW / 2}px;border-radius:${dotW}px;width:${dotW}px;height:${dotW}px;background-color:${color};"></span>`;
            //     const valueHTML = `<span style="margin-left:${dotW * 2}px;font-weight:bolder;">${value}</span>`;
            //     const coreHTML = (marginTop = 0) =>
            //         `<div style="margin-top:${marginTop}px;display:flex;align-items:center;">${dotHTML + name + valueHTML}</div>`;
            //     return type === 'pie' ? coreHTML() : seriesName + coreHTML(dotW / 2);
            // },
        },
    };
};

/** 可以互相转换的图表类型 */
const relativedChartTypes: ChartType[][] = [
    [
        'bar-basic',
        'bar-stacked',
        'bar-horizontal-basic',
        'bar-horizontal-stacked',
        'line-basic',
        'pie-basic',
        'pie-doughnut',
    ],
];

export const getAvailableChartTypes = (chartType: ChartType, reserveSelf = false): ChartType[] =>
    relativedChartTypes
        .filter((ls) => ls.includes(chartType))
        .flat()
        ?.filter((ls) => (reserveSelf ? true : ls !== chartType)) ?? [];

export const changeChartByType = (chartOptions: IChartOptions, type: ChartType): IChartOptions => {
    const originalType = chartOptions.type;
    const availableTypes = getAvailableChartTypes(originalType);
    if (!availableTypes.includes(type)) {
        throw new Error(`No available chart type for ${type}`);
    }

    const newChartOptions = cloneDeep(chartOptions);
    newChartOptions.type = type;

    const isHorizontalChange =
        (chartOptions.type.includes('horizontal') && !type.includes('horizontal')) ||
        (!chartOptions.type.includes('horizontal') && type.includes('horizontal'));

    if (isHorizontalChange) {
        const { categoryAxisLabel, valueAxisLabel } = newChartOptions;
        newChartOptions.categoryAxisLabel = valueAxisLabel;
        newChartOptions.valueAxisLabel = categoryAxisLabel;
    }

    const isOther2Pie = !originalType.includes('pie') && type.includes('pie');
    const isPie2Other = originalType.includes('pie') && !type.includes('pie');

    // 饼图与其他类型图的颜色坐标轴不一致, 这里需要修复
    if (isOther2Pie || isPie2Other) {
        newChartOptions.colors = getFixedColorsAfterEdit(newChartOptions);
    }

    return newChartOptions;
};
